// SPDX-License-Identifier: AGPL-3.0-or-later

import Sticker from '@app/features/emoji/state/Sticker';
import * as GuildStickerCommands from '@app/features/expressions/commands/GuildStickerCommands';
import ExpressionMetadata from '@app/features/expressions/state/ExpressionMetadata';
import {GuildIcon} from '@app/features/guild/components/popouts/GuildIcon';
import Guilds from '@app/features/guild/state/Guilds';
import Permission from '@app/features/permissions/state/Permission';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {failureValidationErrors} from '@app/features/platform/utils/ResponseInspection';
import {MenuGroup} from '@app/features/ui/action_menu/MenuGroup';
import {MenuItem} from '@app/features/ui/action_menu/MenuItem';
import {MenuItemSubmenu} from '@app/features/ui/action_menu/MenuItemSubmenu';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import * as AvatarUtils from '@app/features/user/utils/AvatarUtils';
import {createKeyedActionGuard} from '@app/lib/overlay/keyedActionGuard';
import {Permissions} from '@app/vendor/constants/ChannelConstants';
import {useLingui} from '@lingui/react/macro';
import {CopyIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect} from 'react';

const logger = new Logger('CloneSticker');

const stickerCloneGuard = createKeyedActionGuard();
export type CloneableSticker = Readonly<{
	id: string;
	guildId: string;
	name: string;
	description?: string | null;
	tags?: ReadonlyArray<string>;
	animated: boolean;
}>;
async function blobToDataUrl(blob: Blob): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(String(reader.result));
		reader.onerror = () => reject(new Error('Failed to read image'));
		reader.readAsDataURL(blob);
	});
}
interface CloneStickerMenuItemProps {
	sticker: CloneableSticker;
	onClose: () => void;
}
export const CloneStickerMenuItem: React.FC<CloneStickerMenuItemProps> = observer(({sticker, onClose}) => {
	const {t} = useLingui();
	const cloneName = GuildStickerCommands.sanitizeStickerName(sticker.name);
	const sourceGuild = sticker.guildId ? Guilds.getGuild(sticker.guildId) : null;
	const metadataState = ExpressionMetadata.getStickerMetadata(sticker.id);
	const needsMetadataLookup = !sourceGuild;
	useEffect(() => {
		if (needsMetadataLookup && !metadataState.data && !metadataState.loading && !metadataState.error) {
			void ExpressionMetadata.fetchStickerMetadata(sticker.id);
		}
	}, [needsMetadataLookup, metadataState.data, metadataState.loading, metadataState.error, sticker.id]);
	const cloningAllowed = sourceGuild
		? sourceGuild.cloneStickerAllowed
		: metadataState.data
			? metadataState.data.allowCloning
			: null;
	const isLoadingPermission = needsMetadataLookup && metadataState.loading;
	if (cloningAllowed === false) return null;
	const eligible = Guilds.getGuilds()
		.filter((guild) => guild.id !== sticker.guildId)
		.filter((guild) => Permission.can(Permissions.MANAGE_EXPRESSIONS, {guildId: guild.id}))
		.map((guild) => {
			const targetStickers = Sticker.getGuildStickers(guild.id);
			const used = targetStickers.length;
			return {guild, slotsFull: used >= guild.maxStickers};
		})
		.sort((a, b) => a.guild.name.localeCompare(b.guild.name));
	if (eligible.length === 0) return null;
	const cloneInto = async (guildId: string) => {
		const targetGuild = Guilds.getGuild(guildId);
		const targetName = targetGuild?.name ?? '';
		const inFlightKey = `${sticker.id}:${guildId}`;
		if (!stickerCloneGuard.begin(inFlightKey)) return;
		try {
			const sourceUrl = AvatarUtils.getStickerURL({id: sticker.id, animated: sticker.animated, size: 320});
			const response = await fetch(sourceUrl);
			if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);
			const blob = await response.blob();
			const image = await blobToDataUrl(blob);
			await GuildStickerCommands.create(guildId, {
				name: cloneName,
				description: sticker.description ?? '',
				tags: sticker.tags && sticker.tags.length > 0 ? [...sticker.tags] : [],
				image,
			});
			ToastCommands.success(t`Cloned sticker ${sticker.name} into ${targetName}`);
		} catch (error) {
			logger.error(`Failed to clone sticker ${sticker.id} into ${guildId}`, error);
			const apiErrors = failureValidationErrors(error);
			const message = apiErrors?.map((e) => e.message).join(', ') ?? (error instanceof Error ? error.message : null);
			ToastCommands.error(message ?? t`Failed to clone sticker into ${targetName}`);
		} finally {
			stickerCloneGuard.scheduleRelease(inFlightKey);
		}
	};
	if (isLoadingPermission) {
		return (
			<MenuGroup>
				<MenuItem icon={<CopyIcon />} disabled hint={t`Checking permissions...`}>
					{t`Clone Sticker into...`}
				</MenuItem>
			</MenuGroup>
		);
	}
	return (
		<MenuGroup>
			<MenuItemSubmenu
				label={t`Clone Sticker into...`}
				render={() => (
					<MenuGroup>
						{eligible.map(({guild, slotsFull}) => (
							<MenuItem
								key={guild.id}
								icon={<GuildIcon id={guild.id} name={guild.name} icon={guild.icon} sizePx={16} />}
								disabled={slotsFull}
								hint={slotsFull ? t`Sticker slots full` : undefined}
								onClick={() => {
									void cloneInto(guild.id);
									onClose();
								}}
							>
								{guild.name}
							</MenuItem>
						))}
					</MenuGroup>
				)}
			/>
		</MenuGroup>
	);
});
